import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import ClientDetails from '../../components/ProjectInfo/ClientDetails';
import JobDetails from '../../components/ProjectInfo/JobDetails';
import SkillsInfo from '../../components/ProjectInfo/SkillsInfo';
import ProgressDetails from '../../components/ProjectInfo/ProgressDetails';
import MatchedTalentsTable from '../../components/Table/MatchedTalentsTable';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useGetAHireRequestByIdQuery, useApproveJobMutation, useRejectJobMutation } from '../../reactWrapper/Redux/apiSlice';
import { Button } from '../../components/ui/button';
import ControlledModal from '../../components/Modal/ControlledModal';
import { toast } from 'react-toastify';

/* Sections were separated by bare <Divider>s on the page background. The
   reference puts every content block in its own card instead (rule 1/2). */
const Card = ({ children }) => (
  <section className='rounded-2xl border bg-card p-5 shadow-ui-sm'>
    {children}
  </section>
);

const CompanyDetails = () => {
  const {id} = useParams();
  const {data, refetch} = useGetAHireRequestByIdQuery(id)
  const [approveJob, { isLoading: isApproving }] = useApproveJobMutation();
  const [rejectJob, { isLoading: isRejecting }] = useRejectJobMutation();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const isPending = data?.status === 'pending' && data?.admin_status !== 'approved' && data?.admin_status !== 'rejected';
  const isRejected = data?.status === 'rejected';

  const handleApprove = async () => {
    try {
      await approveJob(id).unwrap();
      toast.success('Job approved successfully');
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to approve job');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await rejectJob({ id, rejection_reason: rejectReason }).unwrap();
      toast.success('Job rejected');
      setShowRejectModal(false);
      setRejectReason('');
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to reject job');
    }
  };

  return (
    <div className='mx-auto max-w-6xl space-y-6'>
      {isRejected && data?.rejection_reason && (
        <div className='flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800'>
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          <span>
            <span className='font-semibold'>Rejection reason: </span>
            {data.rejection_reason}
          </span>
        </div>
      )}

      <Card>
        <ClientDetails clientsdetails={data} />
      </Card>

      <Card>
        <SkillsInfo SkillsInfo={data} />
      </Card>

      <Card>
        <JobDetails jobdetails={data} />
      </Card>

      {isPending && (
        <div className='flex items-center justify-end gap-2 rounded-2xl border bg-card p-4 shadow-ui-sm'>
          <Button
            onClick={() => setShowRejectModal(true)}
            disabled={isRejecting}
            className='border border-red-300 bg-transparent text-red-700 hover:bg-red-50'
          >
            Reject
          </Button>
          <Button
            onClick={handleApprove}
            disabled={isApproving}
            className='bg-brand-orange text-white hover:bg-brand-orange-600'
          >
            {isApproving ? 'Approving...' : 'Approve'}
          </Button>
        </div>
      )}

      <Card>
        <ProgressDetails talents={data?.jobDetailMatchedTalents} />
      </Card>

      {data?.id && (
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold text-brand-navy'>
            Matched Talents
          </h2>
          <MatchedTalentsTable jobId={data.id} />
        </div>
      )}

      <ControlledModal
        open={showRejectModal}
        closeModal={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        contentStyle={{ width: '450px', borderRadius: '12px', padding: '24px' }}
      >
        <div className='flex flex-col gap-4 rounded-2xl bg-card p-6'>
          <div>
            <h1 className='text-lg font-bold text-brand-navy'>Reject Job</h1>
            <p className='mt-1 text-sm text-muted-foreground'>
              Provide a reason for rejecting this job:
            </p>
          </div>

          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className='min-h-[120px] w-full rounded-lg border border-input bg-transparent p-3 text-sm shadow-ui-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            placeholder='Enter rejection reason...'
          />

          <div className='mt-2 flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end'>
            <Button
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
              }}
              className='border border-brand-navy bg-transparent text-brand-navy hover:bg-surface'
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isRejecting || !rejectReason.trim()}
              className='bg-red-600 text-white hover:bg-red-700'
            >
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        </div>
      </ControlledModal>
    </div>
  );
};

export default CompanyDetails;
